import holidays from '@/components/layout/holidays'

interface HolidayDate {
  day: number
  month: number
  year: number | null
}

interface Holiday {
  startDate: HolidayDate
  endDate: HolidayDate
  theme?: string
  [key: string]: any
}

function isDateInRange(date: Date, startDate: HolidayDate, endDate: HolidayDate): boolean {
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const currentDay = date.getDate()

  const startMonth = startDate.month
  const startDay = startDate.day
  const startYear = startDate.year

  const endMonth = endDate.month
  const endDay = endDate.day
  const endYear = endDate.year

  if (startYear !== null && currentYear < startYear) {
    return false
  }
  if (endYear !== null && currentYear > endYear) {
    return false
  }

  if (startYear !== null && endYear !== null && startYear !== endYear) {
    if (currentYear > startYear && currentYear < endYear) {
      return true
    }
    if (currentYear === startYear) {
      if (currentMonth > startMonth) { return true }
      if (currentMonth === startMonth && currentDay >= startDay) { return true }
      return false
    }
    if (currentYear === endYear) {
      if (currentMonth < endMonth) { return true }
      if (currentMonth === endMonth && currentDay <= endDay) { return true }
      return false
    }
    return false
  }

  if (startMonth <= endMonth) {
    if (currentMonth < startMonth || currentMonth > endMonth) {
      return false
    }
    if (currentMonth === startMonth && currentDay < startDay) {
      return false
    }
    if (currentMonth === endMonth && currentDay > endDay) {
      return false
    }
    return true
  } else {
    if (currentMonth > endMonth && currentMonth < startMonth) {
      return false
    }
    if (currentMonth === startMonth && currentDay < startDay) {
      return false
    }
    if (currentMonth === endMonth && currentDay > endDay) {
      return false
    }
    return true
  }
}

export function getActiveHoliday(date = new Date()): Holiday | null {
  const holidayList = Object.values(holidays) as Holiday[]
  for (const holiday of holidayList) {
    if (isDateInRange(date, holiday.startDate, holiday.endDate)) {
      return holiday
    }
  }
  return null
}

export function getHolidayById(holidayId: string): Holiday | null {
  return (holidays as Record<string, Holiday>)[holidayId] || null
}

export function isHolidayActive(holidayId: string, date = new Date()): boolean {
  const holiday = getHolidayById(holidayId)
  if (!holiday) {
    return false
  }
  return isDateInRange(date, holiday.startDate, holiday.endDate)
}

export function isThemeValid(themeCode: string, date = new Date()): boolean {
  const holidayList = Object.values(holidays) as Holiday[]
  const matchedHoliday = holidayList.find(h => h.theme === themeCode)

  if (!matchedHoliday) {
    return true
  }

  return isDateInRange(date, matchedHoliday.startDate, matchedHoliday.endDate)
}

export function getAllHolidays(): Record<string, Holiday> {
  return holidays as Record<string, Holiday>
}

export interface HolidayPlugin {
  getActive: typeof getActiveHoliday
  getById: typeof getHolidayById
  isActive: typeof isHolidayActive
  isThemeValid: typeof isThemeValid
  getAll: typeof getAllHolidays
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.provide('holiday', {
    getActive: getActiveHoliday,
    getById: getHolidayById,
    isActive: isHolidayActive,
    isThemeValid,
    getAll: getAllHolidays,
  } as HolidayPlugin)
})
